import { Controller } from "@hotwired/stimulus";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { addMapBackground } from "../utils/map_background";

// Live map of the school buses (GO4SFLEET, 20/10/2026).
//
// Reads a JSON document — from `urlValue`, polled every `intervalValue`
// seconds, or once from the inline `data` target — shaped as:
//   { buses: [{ label, latitude, longitude, recorded_at, stale, url, detail }],
//     stops: [{ name, latitude, longitude, stop_order, highlight }],
//     trail: [[lat, lng], ...], message: "text shown under the map" }
// Buses are round markers (grey when their position is old), stops small
// numbered dots, the trail a line. The view fits the buses the first time,
// then stays where the user moved it.
export default class extends Controller {
  static targets = ["map", "data", "empty", "updated", "message"];

  static values = {
    url: String,
    interval: { type: Number, default: 15 },
    // Empty: the OpenFreeMap style (utils/map_background.js).
    styleUrl: String,
    tileUrl: String,
    attribution: String,
    staleLabel: { type: String, default: "Position ancienne" },
    updatedLabel: { type: String, default: "Mis à jour à" },
    center: { type: Array, default: [3.848, 11.502] },
  };

  connect() {
    this.map = L.map(this.mapTarget, { zoomControl: false, scrollWheelZoom: true })
      .setView(this.centerValue, 12);
    L.control.zoom({ position: "bottomright" }).addTo(this.map);
    addMapBackground(this.map, { styleUrl: this.styleUrlValue, tileUrl: this.tileUrlValue, attribution: this.attributionValue });
    this.layer = L.layerGroup().addTo(this.map);
    this.fitted = false;

    if (this.hasDataTarget) {
      this.render(JSON.parse(this.dataTarget.textContent || "{}"));
    }
    if (this.hasUrlValue && this.urlValue) {
      this.refresh();
      this.timer = setInterval(() => this.refresh(), Math.max(this.intervalValue, 5) * 1000);
    }
  }

  disconnect() {
    if (this.timer) clearInterval(this.timer);
    if (this.map) this.map.remove();
    this.map = null;
  }

  async refresh() {
    try {
      const response = await fetch(this.urlValue, { headers: { Accept: "application/json" }, credentials: "same-origin" });
      if (!response.ok) return;
      this.render(await response.json());
    } catch (_error) {
      // The next tick tries again: a lost connection must not break the page.
    }
  }

  render(data) {
    if (!this.map) return;
    this.layer.clearLayers();
    const points = [];

    (data.trail || []).length > 1 &&
      L.polyline(data.trail, { color: "#29ac46", weight: 4, opacity: 0.7 }).addTo(this.layer);

    (data.stops || []).forEach((stop) => {
      if (stop.latitude == null || stop.longitude == null) return;
      const marker = L.circleMarker([stop.latitude, stop.longitude], {
        radius: stop.highlight ? 9 : 6,
        color: stop.highlight ? "#1d4ed8" : "#64748b",
        fillColor: "#fff",
        fillOpacity: 1,
        weight: stop.highlight ? 4 : 2,
      }).addTo(this.layer);
      marker.bindTooltip(`${stop.stop_order ? `${stop.stop_order}. ` : ""}${this.escape(stop.name)}`);
      points.push([stop.latitude, stop.longitude]);
    });

    (data.buses || []).forEach((bus) => {
      if (bus.latitude == null || bus.longitude == null) return;
      const color = bus.stale ? "#94a3b8" : "#29ac46";
      const icon = L.divIcon({
        className: "",
        html: `<div style="display:flex;align-items:center;justify-content:center;width:34px;height:34px;border-radius:9999px;background:${color};border:3px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.3);color:#fff;font-size:16px;">🚌</div>`,
        iconSize: [34, 34],
        iconAnchor: [17, 17],
      });
      const marker = L.marker([bus.latitude, bus.longitude], { icon }).addTo(this.layer);
      const time = bus.recorded_at ? new Date(bus.recorded_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "";
      const lines = [`<strong>${this.escape(bus.label || "")}</strong>`];
      if (bus.detail) lines.push(this.escape(bus.detail));
      if (time) lines.push(`${this.escape(this.updatedLabelValue)} ${time}${bus.stale ? ` — ${this.escape(this.staleLabelValue)}` : ""}`);
      if (bus.url) lines.push(`<a href="${bus.url}">→</a>`);
      marker.bindPopup(lines.join("<br>"));
      points.push([bus.latitude, bus.longitude]);
    });

    if (this.hasMessageTarget && data.message !== undefined) this.messageTarget.textContent = data.message || "";
    if (this.hasEmptyTarget) this.emptyTarget.hidden = (data.buses || []).length > 0;
    if (this.hasUpdatedTarget) this.updatedTarget.textContent = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });

    if (!this.fitted && points.length) {
      this.bounds = L.latLngBounds(points).pad(0.2);
      this.map.fitBounds(this.bounds, { maxZoom: 16 });
      this.fitted = true;
      // The card may still be laying out: measure again, then frame again.
      setTimeout(() => {
        if (!this.map) return;
        this.map.invalidateSize();
        this.map.fitBounds(this.bounds, { maxZoom: 16 });
      }, 250);
    }
  }

  escape(text) {
    const div = document.createElement("div");
    div.textContent = text == null ? "" : String(text);
    return div.innerHTML;
  }
}
